import type {
	AuditEntry,
	Cause,
	MatrixAnswer,
	Occurrence,
	OccurrenceAction,
} from '@/types/occurrence'

const EMPTY_INVESTIGATION = {
	method: null,
	recommended: null,
	participants: [],
	causes: [],
	whys: [],
}

const EMPTY_MATRIX = {
	version: 'MI v1 (mock)',
	answers: [],
	result: null,
	factors: [],
	validatedBy: null,
	validatedAt: null,
	committeeNote: '',
}

export function blankOccurrence(
	partial: Partial<Occurrence> & Pick<Occurrence, 'id' | 'number'>,
): Occurrence {
	return {
		type: 'WORK_ACCIDENT',
		status: 'DRAFT',
		date: '2026-10-08',
		time: '',
		branch: '',
		contract: '',
		customer: '',
		cr: '',
		description: '',
		fullDescription: '',
		potential: 'N5',
		proposedClassification: null,
		classification: null,
		bodyPart: 'NA',
		lifeAttitude: 'NA',
		equated: false,
		workRelated: true,
		state: '',
		city: '',
		cep: '',
		activityFrequency: 'Não planejada',
		workType: 'Manutenção',
		serviceType: 'Acessos',
		mainActivity: false,
		workSchedule: '',
		createdBy: 'Luana Ventura Alves Ferreira',
		createdAt: '2026-10-08T09:00:00',
		employee: null,
		evidences: [],
		testimonies: [],
		committee: [],
		timeline: [],
		investigation: { ...EMPTY_INVESTIGATION },
		matrix: { ...EMPTY_MATRIX },
		actions: [],
		lesson: null,
		documents: [],
		medical: null,
		closure: { decidedBy: null, decidedAt: null, note: '' },
		statusHistory: [],
		audit: [],
		...partial,
	}
}

const aud = (
	id: string,
	at: string,
	by: string,
	event: string,
	extra: Partial<AuditEntry> = {},
): AuditEntry => ({
	id,
	at,
	by,
	profile: 'TECHNICIAN',
	event,
	...extra,
})

const act = (
	partial: Partial<OccurrenceAction> &
		Pick<OccurrenceAction, 'id' | 'description'>,
): OccurrenceAction => ({
	origin: 'Ocorrência',
	causeId: null,
	responsible: 'Ventura Alves Ferreira, Luana',
	approver: 'Jonathan Carboni Henck',
	dueDate: '2026-10-31',
	status: 'IN_PROGRESS',
	efficacy: null,
	evidence: [],
	history: [],
	...partial,
})

// ─── #2101 — caso da tela de resumo do PowerApps (PS, em investigação) ──────
const o2101: Occurrence = blankOccurrence({
	id: 'occ-2101',
	number: 2101,
	status: 'INVESTIGATING',
	date: '2026-09-21',
	time: '16:35',
	branch: 'Rio de Janeiro',
	contract: 'Vale S.A - Tig - Acessos',
	customer: 'Vale S.A',
	cr: '2101',
	potential: 'N5',
	proposedClassification: 'PS',
	classification: 'PS',
	bodyPart: 'Rosto',
	description:
		'O empregado M. W. colidiu dispositivo de proteção (titronic) contra o lado esquerdo do rosto durante a retirada, ocasionando escoriação superficial.',
	fullDescription:
		'O empregado M. W. estava realizando a preparação de materiais de andaime no local VV2 – AB2, para atendimento à área 1B. Durante a retirada de um dispositivo de proteção (titronic) da correia TC1A, o empregado veio a colidir o referido dispositivo contra o lado esquerdo do rosto, ocasionando uma escoriação superficial.',
	state: 'Rio de Janeiro (RJ)',
	city: 'Mangaratiba',
	cep: '23825410',
	workSchedule: '08:25',
	workType: 'Manutenção',
	serviceType: 'Acessos',
	employee: {
		name: 'Max William Eneas Silva',
		badge: '911540',
		jobRole: 'Mont Andaime',
		admission: '2025-06-25',
		age: 46,
		education: 'Ensino fundamental completo',
		pda: '62',
	},
	evidences: [
		{
			id: 'e1',
			caption: 'Local da retirada da proteção',
			author: 'Luana Ventura',
			date: '2026-09-21',
			origin: 'Celular',
			confidentiality: 'INTERNAL',
			tone: 'from-slate-700 to-amber-700',
		},
		{
			id: 'e2',
			caption: 'Dispositivo de proteção (titronic)',
			author: 'Luana Ventura',
			date: '2026-09-21',
			origin: 'Celular',
			confidentiality: 'INTERNAL',
			tone: 'from-zinc-600 to-slate-400',
		},
		{
			id: 'e3',
			caption: 'Lesão — escoriação superficial',
			author: 'Dra. Helena Medicina',
			date: '2026-09-21',
			origin: 'Ambulatório',
			confidentiality: 'RESTRICTED',
			tone: 'from-rose-300 to-orange-200',
		},
	],
	testimonies: [
		{
			id: 't1',
			person: 'Max William Eneas Silva',
			role: 'Acidentado',
			date: '2026-09-22',
			fileName: 'depoimento-max.pdf',
			ocrText:
				'Eu estava retirando a proteção da correia para agilizar a montagem quando ela escapou e bateu no meu rosto...',
			ocrValidatedBy: 'Jonathan Carboni Henck',
		},
		{
			id: 't2',
			person: 'Wagner Oliveira',
			role: 'Encarregado',
			date: '2026-09-22',
			fileName: 'depoimento-wagner.pdf',
			ocrText:
				'A retirada de proteções não é autorizada sem solicitar à VALE...',
			ocrValidatedBy: null,
		},
	],
	committee: [
		{
			id: 'c1',
			name: 'Jonathan Carboni Henck',
			role: 'Gestor SSMA',
			area: 'SSMA',
		},
		{ id: 'c2', name: 'Patrícia Gestora', role: 'Gestor', area: 'Operações' },
		{ id: 'c3', name: 'Wagner Oliveira', role: 'Encarregado', area: 'Campo' },
	],
	timeline: [
		{
			id: 'tl1',
			factAt: '2026-09-21T16:35',
			recordedAt: '2026-09-21T17:10',
			description: 'Empregado colide proteção contra o rosto',
			author: 'Luana Ventura',
			origin: 'MANUAL',
		},
		{
			id: 'tl2',
			factAt: '2026-09-21T16:45',
			recordedAt: '2026-09-21T17:12',
			description: 'Atendimento médico no ambulatório',
			author: 'Luana Ventura',
			origin: 'MANUAL',
			evidenceRef: 'e3',
		},
		{
			id: 'tl3',
			factAt: '2026-09-21T17:10',
			recordedAt: '2026-09-21T17:10',
			description: 'Ocorrência registrada no sistema',
			author: 'Sistema',
			origin: 'SYSTEM',
		},
	],
	investigation: {
		method: 'FIVE_WHYS',
		recommended: 'FIVE_WHYS',
		participants: ['Jonathan Carboni Henck', 'Wagner Oliveira'],
		whys: [
			'O empregado foi atingido pela proteção — porque a retirou sem apoio.',
			'Por que retirou? — Para agilizar a atividade.',
			'Por que precisava agilizar? — Pressão de prazo da área 1B.',
			'Por que a pressão não foi tratada? — Planejamento não previu o tempo de solicitação à VALE.',
			'Por que o planejamento não previu? — Procedimento não define o tempo da solicitação.',
		],
		causes: [
			{
				id: 'ca1',
				text: 'Para agilizar a atividade retirou a proteção',
				category: '11.3 Fatores humanos/ergonômicos inadequados',
				parentId: null,
				isRoot: true,
			},
		],
	},
	actions: [
		act({
			id: 'a1',
			description: 'Realizar a divulgação do evento',
			causeId: 'ca1',
			dueDate: '2026-09-25',
			status: 'DONE',
			evidence: ['divulgacao.pdf'],
			history: [
				{
					at: '2026-09-25T10:00',
					by: 'Luana Ventura',
					text: 'Concluída com evidência',
				},
				{
					at: '2026-09-25T15:00',
					by: 'Jonathan Carboni Henck',
					text: 'Aprovada',
				},
			],
		}),
		act({
			id: 'a2',
			description:
				'Realizar a divulgação para todo efetivo da proibição de proteções (titronic) em qualquer situação, na necessidade solicitar a VALE',
			causeId: 'ca1',
			dueDate: '2026-10-16',
		}),
		act({
			id: 'a3',
			description:
				'Solicitado à VALE que as proteções (titronic) devem todas fixadas com parafusos',
			causeId: 'ca1',
			dueDate: '2026-10-31',
		}),
		act({
			id: 'a4',
			description:
				'Solicitado à VALE a instalação de placas de sinalização advertindo que é PROIBIDA A RETIRADA DESTA PROTEÇÃO POR PESSOAS NÃO AUTORIZADAS',
			causeId: 'ca1',
			dueDate: '2026-10-31',
		}),
	],
	medical: {
		careDate: '2026-09-21',
		careTime: '16:45',
		doctor: 'Micael Aparecido Fidelis',
		crm: '52-895644 - RJ',
		cid: 'S008',
		bodySide: 'Esquerdo',
		externalTreatment: 'Sim',
		bodyPart: 'Rosto',
		causalLink: 'Relacionado ao trabalho',
		catIssued: false,
		leaveDays: 0,
		records: [
			{
				id: 'm1',
				date: '2026-09-21',
				subject: 'Foto da lesão',
				note: 'Escoriação superficial, sem necessidade de sutura.',
				by: 'Dra. Helena Medicina',
			},
		],
	},
	createdAt: '2026-09-21T17:10:00',
	statusHistory: [
		{
			at: '2026-09-21T17:10',
			by: 'Luana Ventura',
			from: 'DRAFT',
			to: 'REGISTERED',
		},
		{
			at: '2026-09-21T17:30',
			by: 'Luana Ventura',
			from: 'REGISTERED',
			to: 'AWAITING_CLASSIFICATION',
		},
		{
			at: '2026-09-22T09:00',
			by: 'Jonathan Carboni Henck',
			from: 'AWAITING_CLASSIFICATION',
			to: 'INVESTIGATING',
		},
	],
	audit: [
		aud('au1', '2026-09-21T17:10', 'Luana Ventura', 'Ocorrência criada'),
		aud(
			'au2',
			'2026-09-22T09:00',
			'Jonathan Carboni Henck',
			'Classificação confirmada',
			{
				profile: 'SSMA_MANAGER',
				diff: { field: 'classificação', from: '—', to: 'PS' },
			},
		),
		aud(
			'au3',
			'2026-09-23T11:20',
			'Dra. Helena Medicina',
			'Consulta a dados médicos',
			{ profile: 'MEDICINE', sensitive: true },
		),
	],
})

// ─── #2096 — plano de ação em andamento ─────────────────────────────────────
const o2096: Occurrence = blankOccurrence({
	id: 'occ-2096',
	number: 2096,
	status: 'ACTION_PLAN',
	date: '2026-09-17',
	time: '10:20',
	branch: 'SP Interior',
	contract: 'RHODIA',
	cr: '3120',
	potential: 'N5',
	proposedClassification: 'PS',
	classification: 'PS',
	bodyPart: 'Braço',
	description:
		'Durante a pintura, o colaborador encostou em um vaso com gotejamento de produto, tendo contato com o antibraço.',
	fullDescription:
		'Durante a execução das atividades de pintura o colaborador encostou em um vaso onde estava com gotejamento de produto, tendo contato com o antibraço. Atendimento imediato no ambulatório.',
	workType: 'Parada',
	serviceType: 'Pintura',
	employee: {
		name: 'João da Silva Santos',
		badge: '912201',
		jobRole: 'Pintor Industrial',
		admission: '2023-02-10',
		age: 38,
		education: 'Ensino médio completo',
		pda: '87',
	},
	evidences: [
		{
			id: 'e1',
			caption: 'Lesão no antebraço',
			author: 'Técnico',
			date: '2026-09-17',
			origin: 'Celular',
			confidentiality: 'RESTRICTED',
			tone: 'from-rose-400 to-amber-300',
		},
	],
	testimonies: [
		{
			id: 't1',
			person: 'João da Silva Santos',
			role: 'Acidentado',
			date: '2026-09-18',
			fileName: 'dep-joao.pdf',
			ocrText: 'Encostei no vaso sem perceber o gotejamento.',
			ocrValidatedBy: 'Jonathan Carboni Henck',
		},
	],
	committee: [
		{
			id: 'c1',
			name: 'Jonathan Carboni Henck',
			role: 'Gestor SSMA',
			area: 'SSMA',
		},
	],
	investigation: {
		method: 'CAUSE_TREE',
		recommended: 'CAUSE_TREE',
		participants: ['Jonathan Carboni Henck'],
		whys: [],
		causes: [
			{
				id: 'ev',
				text: 'Contato com produto químico no antebraço',
				parentId: null,
				logic: 'AND',
				isRoot: false,
			},
			{
				id: 'c1',
				text: 'Vaso com gotejamento não sinalizado',
				parentId: 'ev',
				logic: 'OR',
				isRoot: false,
			},
			{
				id: 'c2',
				text: 'Falha na inspeção prévia da área',
				parentId: 'c1',
				isRoot: true,
				category: 'Método',
			},
			{
				id: 'c3',
				text: 'EPI de manga longa não utilizado',
				parentId: 'ev',
				isRoot: true,
				category: 'Mão de obra',
			},
		],
	},
	matrix: {
		version: 'MI v1 (mock)',
		answers: [
			{
				questionId: 'MI-01',
				answer: 'YES',
				justification: 'Procedimento PT-PINT-04 vigente.',
			},
			{
				questionId: 'MI-02',
				answer: 'YES',
				justification: 'Treinamento válido até 03/2027.',
			},
			{
				questionId: 'MI-03',
				answer: 'YES',
				justification: 'Demonstrou na entrevista.',
			},
			{
				questionId: 'MI-04',
				answer: 'NO',
				justification: 'Gotejamento não era visível.',
			},
			{
				questionId: 'MI-05',
				answer: 'YES',
				justification: 'Outro pintor teria agido igual.',
			},
			{
				questionId: 'MI-06',
				answer: 'YES',
				justification: 'Área sem sinalização e iluminação ruim.',
			},
			{
				questionId: 'MI-10',
				answer: 'NO',
				justification: 'Sem fatores individuais.',
			},
			{
				questionId: 'MI-11',
				answer: 'YES',
				justification: 'Evidências coerentes.',
			},
		],
		result: 'ERROR',
		factors: ['Sinalização insuficiente'],
		validatedBy: 'Jonathan Carboni Henck',
		validatedAt: '2026-09-25T14:00',
		committeeNote: 'Comitê validou: erro induzido por condição do sistema.',
	},
	actions: [
		act({
			id: 'a1',
			description: 'Instalar sinalização e bandeja coletora nos vasos',
			causeId: 'c2',
			responsible: 'Wagner Oliveira',
			status: 'AWAITING_APPROVAL',
			dueDate: '2026-10-05',
			evidence: ['foto-sinalizacao.jpg'],
			history: [
				{
					at: '2026-10-04T16:00',
					by: 'Wagner Oliveira',
					text: 'Concluída e enviada para aprovação',
				},
			],
		}),
		act({
			id: 'a2',
			description: 'Reforçar uso de manga longa no DDS semanal',
			causeId: 'c3',
			responsible: 'Luana Ventura Alves Ferreira',
			status: 'IN_PROGRESS',
			dueDate: '2026-10-09',
		}),
	],
	createdAt: '2026-09-17T11:00:00',
	statusHistory: [
		{
			at: '2026-09-25T14:00',
			by: 'Jonathan Carboni Henck',
			from: 'AWAITING_VALIDATION',
			to: 'ACTION_PLAN',
		},
	],
	audit: [aud('au1', '2026-09-17T11:00', 'Técnico', 'Ocorrência criada')],
})

// ─── #2092 — aguardando eficácia (quase acidente) ───────────────────────────
const o2092: Occurrence = blankOccurrence({
	id: 'occ-2092',
	number: 2092,
	type: 'NEAR_MISS',
	status: 'AWAITING_EFFICACY',
	date: '2026-09-15',
	time: '14:00',
	branch: 'Grande SP',
	contract: 'Mercedes Benz Serviços',
	cr: '2210',
	potential: 'N3',
	proposedClassification: 'QA',
	classification: 'QA',
	description:
		'Durante a movimentação de uma viga I, a equipe estava realizando a movimentação do piso 0 para o local de reforço da estrutura.',
	fullDescription:
		'Por volta das 14:00, durante a movimentação de uma viga I, a equipe estava realizando a movimentação da viga do piso 0 para o local de reforço, devido a limitações de acesso do local, a viga quase tombou sem atingir pessoas.',
	employee: {
		name: 'Marcos Antônio Pereira',
		badge: '913004',
		jobRole: 'Soldador',
		admission: '2021-08-02',
		age: 41,
		education: 'Ensino técnico',
		pda: '45',
	},
	evidences: [
		{
			id: 'e1',
			caption: 'Ponto de içamento',
			author: 'Técnico',
			date: '2026-09-15',
			origin: 'Celular',
			confidentiality: 'INTERNAL',
			tone: 'from-stone-500 to-amber-600',
		},
	],
	testimonies: [
		{
			id: 't1',
			person: 'Marcos Antônio Pereira',
			role: 'Envolvido',
			date: '2026-09-16',
			fileName: 'dep-marcos.pdf',
			ocrText: 'A viga balançou durante o içamento.',
			ocrValidatedBy: 'Jonathan Carboni Henck',
		},
	],
	committee: [
		{
			id: 'c1',
			name: 'Jonathan Carboni Henck',
			role: 'Gestor SSMA',
			area: 'SSMA',
		},
	],
	investigation: {
		method: 'ISHIKAWA',
		recommended: 'ISHIKAWA',
		participants: ['Jonathan Carboni Henck'],
		whys: [],
		causes: [
			{
				id: 'c1',
				text: 'Plano de içamento sem checagem de centro de gravidade',
				category: 'Método',
				parentId: null,
				isRoot: true,
			},
			{
				id: 'c2',
				text: 'Acesso restrito impediu posicionamento correto',
				category: 'Meio ambiente',
				parentId: null,
				isRoot: true,
			},
		],
	},
	matrix: {
		version: 'MI v1 (mock)',
		answers: [],
		result: 'INCONCLUSIVE',
		factors: [],
		validatedBy: 'Jonathan Carboni Henck',
		validatedAt: '2026-09-20T10:00',
		committeeNote: 'Dispensada pelo comitê.',
	},
	actions: [
		act({
			id: 'a1',
			description: 'Revisar plano de içamento (checklist de CG)',
			causeId: 'c1',
			status: 'DONE',
			efficacy: 'EFFECTIVE',
			efficacyNote: 'Dois içamentos auditados sem desvios.',
			dueDate: '2026-10-01',
			evidence: ['plano-icamento.pdf'],
		}),
		act({
			id: 'a2',
			description: 'Mapear acessos antes das movimentações',
			causeId: 'c2',
			status: 'DONE',
			dueDate: '2026-10-03',
			evidence: ['mapa-acessos.pdf'],
		}),
	],
	createdAt: '2026-09-15T15:00:00',
	statusHistory: [
		{
			at: '2026-10-05T09:00',
			by: 'Jonathan Carboni Henck',
			from: 'ACTION_PLAN',
			to: 'AWAITING_EFFICACY',
		},
	],
	audit: [aud('au1', '2026-09-15T15:00', 'Técnico', 'Ocorrência criada')],
})

// ─── #2089 — aguardando classificação (dano material) ───────────────────────
const o2089: Occurrence = blankOccurrence({
	id: 'occ-2089',
	number: 2089,
	type: 'PROPERTY_DAMAGE',
	status: 'AWAITING_CLASSIFICATION',
	date: '2026-09-14',
	time: '09:40',
	branch: 'Nordeste',
	contract: 'BRASKEM AL - CS1 / PVC2',
	cr: '4410',
	potential: 'N5',
	proposedClassification: 'DANO_MATERIAL',
	description: 'Falha momentânea no sistema de frenagem do veículo da empresa.',
	fullDescription:
		'Colaborador conduzia veículo da empresa quando, ao acionar o freio, percebeu falha momentânea no sistema de frenagem. Para reduzir a velocidade, engatou a primeira marcha e utilizou o freio-motor. Em seguida colidiu levemente na traseira de outro veículo.',
	evidences: [
		{
			id: 'e1',
			caption: 'Dano na traseira do veículo terceiro',
			author: 'Técnico',
			date: '2026-09-14',
			origin: 'Celular',
			confidentiality: 'INTERNAL',
			tone: 'from-sky-400 to-slate-300',
		},
	],
	createdAt: '2026-09-14T10:30:00',
	statusHistory: [
		{
			at: '2026-09-14T10:30',
			by: 'Técnico',
			from: 'REGISTERED',
			to: 'AWAITING_CLASSIFICATION',
		},
	],
	audit: [aud('au1', '2026-09-14T10:30', 'Técnico', 'Ocorrência criada')],
})

// ─── #2090 — encerrada, com lição aprendida ─────────────────────────────────
const o2090: Occurrence = blankOccurrence({
	id: 'occ-2090',
	number: 2090,
	type: 'NEAR_MISS',
	status: 'CLOSED',
	date: '2026-09-14',
	time: '11:15',
	branch: 'Nordeste',
	contract: 'RNEST PINTURA',
	cr: '4420',
	potential: 'N5',
	proposedClassification: 'QA',
	classification: 'QA',
	description:
		'Lixadeira elétrica caiu ao colaborador descer a escada de acesso ao tanque 22001.',
	fullDescription:
		'Lixadeira elétrica caiu ao colaborador descer a escada de acesso ao tanque 22001. A ferramenta caiu em área isolada, sem atingir pessoas ou estruturas. Não houve danos, lesões ou impactos.',
	employee: {
		name: 'Rafael Souza Lima',
		badge: '914777',
		jobRole: 'Pintor Industrial',
		admission: '2026-01-12',
		age: 24,
		education: 'Ensino médio completo',
		pda: '31',
	},
	evidences: [
		{
			id: 'e1',
			caption: 'Escada de acesso ao tanque',
			author: 'Técnico',
			date: '2026-09-14',
			origin: 'Celular',
			confidentiality: 'PUBLIC',
			tone: 'from-orange-500 to-yellow-400',
		},
		{
			id: 'e2',
			caption: 'Ferramenta caída',
			author: 'Técnico',
			date: '2026-09-14',
			origin: 'Celular',
			confidentiality: 'PUBLIC',
			tone: 'from-orange-400 to-amber-300',
		},
	],
	actions: [
		act({
			id: 'a1',
			description: 'Adotar talabarte para ferramentas em altura',
			status: 'DONE',
			efficacy: 'EFFECTIVE',
			dueDate: '2026-09-30',
			evidence: ['talabarte.jpg'],
		}),
	],
	lesson: {
		service: 'Pintura industrial em tanque',
		activity: 'Acesso por escada com ferramenta elétrica',
		summary: 'Lixadeira caiu durante a descida da escada, sem vítimas.',
		learnings:
			'Ferramentas devem ser transportadas por içamento ou presas por talabarte.',
		recommendations:
			'Incluir ancoragem de ferramentas na APR de trabalhos em altura.',
		keywords: ['ferramenta', 'queda de objetos', 'escada'],
		photoIds: ['e1', 'e2'],
		validated: true,
	},
	closure: {
		decidedBy: 'Jonathan Carboni Henck',
		decidedAt: '2026-10-02T10:00',
		note: 'Causas tratadas, ações eficazes. Encerrada.',
	},
	createdAt: '2026-09-14T12:00:00',
	statusHistory: [
		{
			at: '2026-10-02T10:00',
			by: 'Jonathan Carboni Henck',
			from: 'AWAITING_EFFICACY',
			to: 'CLOSED',
		},
	],
	audit: [aud('au1', '2026-09-14T12:00', 'Técnico', 'Ocorrência criada')],
})

// ─── #2105 — registrada (alcoolemia / atitude pela vida) ────────────────────
const o2105: Occurrence = blankOccurrence({
	id: 'occ-2105',
	number: 2105,
	type: 'SSMA_DEVIATION',
	status: 'REGISTERED',
	date: '2026-10-09',
	time: '07:30',
	branch: 'Minas Gerais',
	contract: 'Vale - Eletr.',
	cr: '5110',
	potential: 'N4',
	proposedClassification: 'SITUACAO_INSEGURA',
	lifeAttitude: 'Não realizo atividades sob efeito de álcool ou drogas',
	description:
		'Durante teste de alcoolemia, colaborador apresentou resultado positivo.',
	fullDescription:
		'Durante a realização do controle de alcoolemia, foi detectado resultado positivo para um colaborador, procedimento realizado conforme padrão.',
	createdAt: '2026-10-09T08:00:00',
	statusHistory: [
		{ at: '2026-10-09T08:00', by: 'Técnico', from: 'DRAFT', to: 'REGISTERED' },
	],
	audit: [aud('au1', '2026-10-09T08:00', 'Técnico', 'Ocorrência criada')],
})

// ─── #2110 — rascunho ───────────────────────────────────────────────────────
const o2110: Occurrence = blankOccurrence({
	id: 'occ-2110',
	number: 2110,
	status: 'DRAFT',
	date: '2026-10-10',
	branch: 'Rio de Janeiro',
	description: '',
	createdAt: '2026-10-10T18:00:00',
	audit: [aud('au1', '2026-10-10T18:00', 'Técnico', 'Rascunho criado')],
})

// ─── #2080 — CAF com CAT, aguardando validação ──────────────────────────────
const causes2080: Cause[] = [
	{
		id: 'ev',
		text: 'Queda de altura com fratura de punho',
		parentId: null,
		logic: 'AND',
		isRoot: false,
	},
	{
		id: 'c1',
		text: 'Trabalho em altura sem ancoragem contínua',
		parentId: 'ev',
		logic: 'OR',
		isRoot: false,
	},
	{
		id: 'c2',
		text: 'Ponto de ancoragem não instalado',
		parentId: 'c1',
		isRoot: true,
		category: 'Material',
	},
	{
		id: 'c3',
		text: 'Liberação da área sem checklist',
		parentId: 'c1',
		isRoot: true,
		category: 'Método',
	},
]
const matrix2080: MatrixAnswer[] = [
	{
		questionId: 'MI-01',
		answer: 'YES',
		justification: 'Procedimento de trabalho em altura vigente.',
	},
	{ questionId: 'MI-02', answer: 'YES', justification: 'NR-35 válida.' },
	{
		questionId: 'MI-03',
		answer: 'YES',
		justification: 'Demonstrou conhecimento.',
	},
	{ questionId: 'MI-04', answer: 'YES', justification: 'Queda previsível.' },
	{
		questionId: 'MI-07',
		answer: 'YES',
		justification: 'Soltou o talabarte para alcançar o ponto.',
	},
	{
		questionId: 'MI-08',
		answer: 'YES',
		justification: 'Prática comum na equipe segundo depoimentos.',
	},
	{ questionId: 'MI-10', answer: 'YES', justification: 'Pressão de prazo.' },
	{
		questionId: 'MI-11',
		answer: 'YES',
		justification: 'Evidências suficientes.',
	},
]
const o2080: Occurrence = blankOccurrence({
	id: 'occ-2080',
	number: 2080,
	status: 'AWAITING_VALIDATION',
	date: '2026-09-02',
	time: '13:10',
	branch: 'Rio de Janeiro',
	contract: 'Vale S.A - Tig - Acessos',
	cr: '2101',
	potential: 'N2',
	proposedClassification: 'CAF',
	classification: 'CAF',
	bodyPart: 'Mão',
	description:
		'Queda de altura de 2,5 m durante montagem de andaime, com fratura de punho.',
	fullDescription:
		'Durante a montagem de andaime, o colaborador soltou o talabarte para alcançar o ponto seguinte e caiu de 2,5 m de altura, resultando em fratura de punho.',
	employee: {
		name: 'Rafael Souza Lima',
		badge: '914777',
		jobRole: 'Mont Andaime',
		admission: '2026-01-12',
		age: 24,
		education: 'Ensino médio completo',
		pda: '31',
	},
	evidences: [
		{
			id: 'e1',
			caption: 'Local da queda',
			author: 'Luana Ventura',
			date: '2026-09-02',
			origin: 'Celular',
			confidentiality: 'INTERNAL',
			tone: 'from-slate-600 to-sky-400',
		},
	],
	testimonies: [
		{
			id: 't1',
			person: 'Rafael Souza Lima',
			role: 'Acidentado',
			date: '2026-09-03',
			fileName: 'dep-rafael.pdf',
			ocrText: '...',
			ocrValidatedBy: 'Jonathan Carboni Henck',
		},
	],
	committee: [
		{
			id: 'c1',
			name: 'Jonathan Carboni Henck',
			role: 'Gestor SSMA',
			area: 'SSMA',
		},
		{ id: 'c2', name: 'Patrícia Gestora', role: 'Gestor', area: 'Operações' },
	],
	investigation: {
		method: 'CAUSE_TREE',
		recommended: 'CAUSE_TREE',
		participants: ['Jonathan Carboni Henck'],
		whys: [],
		causes: causes2080,
	},
	matrix: {
		version: 'MI v1 (mock)',
		answers: matrix2080,
		result: 'CIRCUMSTANTIAL_VIOLATION',
		factors: [
			'Fator de liderança/sistema',
			'Fator contribuinte (físico/cognitivo/organizacional/ambiental)',
		],
		validatedBy: null,
		validatedAt: null,
		committeeNote: '',
	},
	medical: {
		careDate: '2026-09-02',
		careTime: '13:40',
		doctor: 'Dr. André Ortopedia',
		crm: '12345-RJ',
		cid: 'S62.0',
		bodySide: 'Direito',
		externalTreatment: 'Sim',
		bodyPart: 'Mão',
		causalLink: 'Relacionado ao trabalho',
		catIssued: true,
		catIssuedAt: '2026-09-03',
		catPdf: 'CAT-2080.pdf',
		leaveDays: 30,
		records: [],
	},
	documents: [
		{
			id: 'd1',
			name: 'CAT-2080.pdf',
			category: 'CAT',
			version: 1,
			confidentiality: 'RESTRICTED',
			retention: '20 anos',
			author: 'Dra. Helena Medicina',
			date: '2026-09-03',
			downloads: [],
		},
	],
	createdAt: '2026-09-02T14:00:00',
	statusHistory: [
		{
			at: '2026-09-28T09:00',
			by: 'Luana Ventura',
			from: 'INVESTIGATING',
			to: 'AWAITING_VALIDATION',
		},
	],
	audit: [aud('au1', '2026-09-02T14:00', 'Luana Ventura', 'Ocorrência criada')],
})

export const SEED_OCCURRENCES: Occurrence[] = [
	o2110,
	o2105,
	o2101,
	o2096,
	o2092,
	o2090,
	o2089,
	o2080,
]
